import {
  endOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import omit from "lodash/omit";
import { useGetIdentity } from "ra-core";

import { Badge } from "@/components/ui/badge";

import type { ActiveFilterBarProps } from "../misc/ActiveFilterBar";
import {
  type FilterDescriptor,
  type FilterOption,
  optionsDescriptor,
  searchDescriptor,
} from "../misc/activeFilters";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useAllTags } from "../tags/useAllTags";
import type { LabeledValue, Tag } from "../types";
import {
  LEGACY_RECORD_TYPE_KEY,
  RECORD_TYPE_FILTER_KEY,
  readRecordTypeFilter,
  writeRecordTypeFilter,
} from "./recordTypeFilterValue";
import {
  TAG_FILTER_KEY,
  readTagFilter,
  writeTagFilter,
} from "./tagFilterValue";

const LAST_SEEN_GTE = "last_seen@gte";
const LAST_SEEN_LTE = "last_seen@lte";

/**
 * The Last activity presets, computed for `now`. The sidebar renders these
 * and the chip matches against them, so both use the same bounds and labels.
 */
export const lastSeenOptions = (now = new Date()): FilterOption[] => [
  {
    label: "resources.contacts.filters.today",
    value: {
      [LAST_SEEN_GTE]: endOfDay(subDays(now, 1)).toISOString(),
      [LAST_SEEN_LTE]: undefined,
    },
  },
  {
    label: "resources.contacts.filters.this_week",
    value: {
      [LAST_SEEN_GTE]: startOfWeek(now).toISOString(),
      [LAST_SEEN_LTE]: undefined,
    },
  },
  {
    label: "resources.contacts.filters.before_this_week",
    value: {
      [LAST_SEEN_GTE]: undefined,
      [LAST_SEEN_LTE]: startOfWeek(now).toISOString(),
    },
  },
  {
    label: "resources.contacts.filters.before_this_month",
    value: {
      [LAST_SEEN_GTE]: undefined,
      [LAST_SEEN_LTE]: startOfMonth(now).toISOString(),
    },
  },
  {
    label: "resources.contacts.filters.before_last_month",
    value: {
      [LAST_SEEN_GTE]: undefined,
      [LAST_SEEN_LTE]: subMonths(startOfMonth(now), 1).toISOString(),
    },
  },
];

const bound = (value: unknown) =>
  value === undefined || value === "" ? undefined : value;

/**
 * One chip whenever either Last activity bound is set.
 *
 * The presets are computed from the clock, so a Today filter set yesterday no
 * longer equals today's Today. Matching the value would drop the chip while the
 * filter still applies; this keys on presence and only uses the preset for the
 * label, falling back to the field name.
 */
export const lastSeenDescriptor = (now = new Date()): FilterDescriptor => ({
  keys: [LAST_SEEN_GTE, LAST_SEEN_LTE],
  chips: (values) => {
    const gte = bound(values[LAST_SEEN_GTE]);
    const lte = bound(values[LAST_SEEN_LTE]);
    if (gte === undefined && lte === undefined) return [];
    const preset = lastSeenOptions(now).find(
      (option) =>
        option.value[LAST_SEEN_GTE] === gte &&
        option.value[LAST_SEEN_LTE] === lte,
    );
    return [
      {
        id: "last_seen",
        label: preset?.label ?? "resources.contacts.fields.last_seen",
        remove: (v) => omit(v, [LAST_SEEN_GTE, LAST_SEEN_LTE]),
      },
    ];
  },
});

/**
 * One chip per selected tag, drawn as the tag's coloured badge.
 *
 * `remove` re-reads the selection from the values it is handed rather than the
 * ones the chip was built from, and writes through `writeTagFilter`, so the
 * any/all mode survives and removing the last tag drops the key.
 *
 * An id with no loaded tag (deleted, or the tags query still in flight) still
 * gets a plain chip: a filter must never be active with nothing showing.
 */
export const tagDescriptor = (tags: readonly Tag[] = []): FilterDescriptor => ({
  keys: [TAG_FILTER_KEY.any, TAG_FILTER_KEY.all],
  chips: (values) => {
    const { ids } = readTagFilter(values);
    return ids.map((id) => {
      const tag = tags.find((t) => String(t.id) === String(id));
      return {
        id: `tag:${id}`,
        label: tag?.name ?? `Tag ${id}`,
        content: tag ? (
          <Badge
            variant="secondary"
            className="text-black text-xs font-normal"
            style={{ backgroundColor: tag.color }}
          >
            {tag.name}
          </Badge>
        ) : undefined,
        remove: (v) => {
          const current = readTagFilter(v);
          return writeTagFilter(
            v,
            current.ids.filter((other) => String(other) !== String(id)),
            current.mode,
          );
        },
      };
    });
  },
});

/**
 * One chip per selected record type, so each can be dropped on its own.
 *
 * A value Settings no longer lists (a type that was deleted, or one a saved
 * filter still carries) keeps a chip under its raw value: the filter is still
 * narrowing the list, so it has to show.
 */
export const recordTypeDescriptor = (
  recordTypes: readonly LabeledValue[] = [],
): FilterDescriptor => ({
  keys: [RECORD_TYPE_FILTER_KEY, LEGACY_RECORD_TYPE_KEY],
  chips: (values) =>
    readRecordTypeFilter(values).map((value) => ({
      id: `record_type:${value}`,
      label: recordTypes.find((type) => type.value === value)?.label ?? value,
      remove: (v) =>
        writeRecordTypeFilter(
          v,
          readRecordTypeFilter(v).filter((other) => other !== value),
        ),
    })),
});

// Linked / not linked are one choice on the same column, so they form one
// group: removing the chip clears whichever is set.
const LOOPS_KEYS = ["loops_contact_id@not.is", "loops_contact_id@is"];
export const LOOPS_OPTIONS: FilterOption[] = [
  {
    label: "resources.contacts.filters.loops_linked",
    value: { "loops_contact_id@not.is": "null" },
  },
  {
    label: "resources.contacts.filters.loops_not_linked",
    value: { "loops_contact_id@is": "null" },
  },
];

/** The contact list's active-filter chips, in sidebar order. */
export const useContactFilterBar = (): ActiveFilterBarProps => {
  const { noteStatuses, recordTypes } = useConfigurationContext();
  const { identity } = useGetIdentity();
  const { data: tags } = useAllTags();

  return {
    descriptors: [
      searchDescriptor(),
      recordTypeDescriptor(recordTypes),
      tagDescriptor(tags),
      optionsDescriptor(
        ["status"],
        noteStatuses.map((status) => ({
          label: status.label,
          value: { status: status.value },
          content: (
            <span>
              {status.label} <Status status={status.value} />
            </span>
          ),
        })),
      ),
      lastSeenDescriptor(),
      optionsDescriptor(LOOPS_KEYS, LOOPS_OPTIONS),
      optionsDescriptor(
        ["sales_id"],
        [{ label: "crm.common.me", value: { sales_id: identity?.id } }],
      ),
    ],
  };
};
